/* eslint-disable unicorn/no-array-for-each */

import {
  Button,
  Card,
  DateFormats,
  Dropdown,
  FileUploader,
  type FileUploadState,
  fileUploadReducer,
  formatISODate,
  Inline,
  Notification,
  notifySuccess,
  type OptionType,
  Spinner,
  Stack,
  Typography,
} from '@omnipresentgroup/design-system';
import {
  useBulkCompensationDownloadTemplateMutation,
  useBulkCompensationMutation,
  useBulkCompensationValidationMutation,
  useDocumentUpload,
  useGetManagerEmployeesSimplified,
} from '@omnipresentgroup/domains';
import {
  BulkCompensationType,
  OnboardingStatusValues,
} from '@omnipresentgroup/types';
import { useCallback, useMemo, useReducer, useState } from 'react';

type StepProps = {
  disabled?: boolean;
};

export const AddBulkSalaryChangesForm = ({
  companyId,
  onSuccess,
}: {
  companyId: string;
  onSuccess?: () => void;
}) => {
  const [selectedEmployees, setSelectedEmployees] = useState<OptionType[]>([]);

  const [state, dispatch] = useReducer(fileUploadReducer, []);

  const [fileUrl, setFileUrl] = useState<string>('');

  const {
    mutate: downloadTemplate,
    isPending: isDownloadingTemplate,
    isSuccess: isSuccessDownloadTemplate,
  } = useBulkCompensationDownloadTemplateMutation();

  const { uploadDocument } = useDocumentUpload();

  const {
    mutate: validate,
    isSuccess: isSuccessValidate,
    isPending: isValidating,
    data: validationData,
  } = useBulkCompensationValidationMutation();

  const { mutate: submitChanges, isPending: isSubmittingChanges } =
    useBulkCompensationMutation({
      onSuccess: () => {
        notifySuccess({ title: 'Salary changes submitted successfully' });
        onSuccess?.();
      },
    });

  const hasValidationErrors = useMemo(
    () => validationData && validationData?.totErrors > 0,
    [validationData],
  );

  const canSubmit = useMemo(
    () => Boolean(fileUrl) && isSuccessValidate && !hasValidationErrors,
    [fileUrl, hasValidationErrors, isSuccessValidate],
  );

  const SelectEmployeesStep = () => {
    const { data: employeesData, isLoading: isLoadingEmployees } =
      useGetManagerEmployeesSimplified({
        companyId,
        enabled: !!companyId,
      });

    const employeeOptions: OptionType[] = useMemo(
      () =>
        employeesData?.employees
          .filter(
            (employee) =>
              employee.onboardingStatus === OnboardingStatusValues.ACTIVE,
          )
          .map((employee) => ({
            id: employee.id,
            value: employee.id,
            label: employee.name,
            description: `Started on ${formatISODate(
              employee.startDate,
              DateFormats.Date,
            )} in ${employee.country} as ${employee.jobTitle?.toLowerCase()}`,
          }))
          .sort((a, b) => a.label.localeCompare(b.label)) || [],
      [employeesData],
    );

    return (
      <Dropdown
        id="employee-selector"
        isMulti
        label="Step 1: Select employees"
        isSearchable
        isLoading={isLoadingEmployees}
        options={[
          {
            options: employeeOptions,
          },
        ]}
        value={selectedEmployees}
        onChange={(newValue) => {
          setSelectedEmployees(
            newValue.map((option) => ({
              ...option,
            })),
          );
        }}
      />
    );
  };

  const DownloadTemplateStep = ({ disabled }: StepProps) => {
    return (
      <Stack gap="4">
        <Typography as="label" weight="medium" size="14">
          Step 2: Download and complete the provided template
        </Typography>
        <Card
          p="24"
          css={{
            backgroundColor: '$background-page',
          }}
        >
          <Inline justify="space-between" align="center">
            <Typography as="span" size="14" color="subtle">
              {selectedEmployees?.length === 0
                ? 'No employees were added to the template'
                : 'The template contains the selected employees'}
            </Typography>
            <Button
              size="small"
              variant="secondary"
              loading={isDownloadingTemplate}
              disabled={disabled}
              onClick={() =>
                downloadTemplate({
                  companyId,
                  employeesIds: selectedEmployees.map((option) =>
                    option.value.toString(),
                  ),
                  compensationType: BulkCompensationType.SALARY,
                })
              }
            >
              Download
            </Button>
          </Inline>
        </Card>
      </Stack>
    );
  };

  const UploadTemplateStep = ({ disabled }: StepProps) => {
    const handleFileUpload = useCallback((file: FileUploadState) => {
      dispatch({ type: 'started', data: file });
      uploadDocument({ fileToUpload: file.props as File })
        .then((res) => {
          dispatch({ type: 'succeeded', data: file });
          setFileUrl(res.documentUrl);
          validate({
            companyId,
            type: BulkCompensationType.SALARY,
            url: res.documentUrl,
          });
        })
        .catch(() => {
          dispatch({ type: 'failed', data: file });
        });
    }, []);

    const handleFileDelete = useCallback((file: FileUploadState) => {
      dispatch({ type: 'deleted', data: file });
      setFileUrl('');
    }, []);

    const onDrop = useCallback(
      (acceptedFiles: File[]) => {
        acceptedFiles.forEach((file, index) => {
          state.forEach((fileState) => {
            dispatch({ type: 'deleted', data: fileState });
          });
          handleFileUpload({ id: index.toString(), props: file });
        });
      },
      [handleFileUpload],
    );

    return (
      <Stack>
        <FileUploader
          id="upload-template"
          files={state}
          fileDropInputProps={{
            id: 'upload-template-file-drop',
            label: 'Step 3: Upload and verify the completed template',
            dropzoneOptions: {
              accept: {
                'text/csv': ['.csv'],
              },
              maxFiles: 1,
              onDrop,
            },
            disabled: disabled,
          }}
          handleFileUpload={handleFileUpload}
          handleFileDelete={handleFileDelete}
        />
        {isValidating && (
          <Inline justify={'center'}>
            <Spinner size="40" />
          </Inline>
        )}
        {isSuccessValidate && (
          <Notification
            id="verification-notice"
            type="inline"
            intent={hasValidationErrors ? 'error' : 'success'}
            title={
              hasValidationErrors
                ? 'Verification failed'
                : 'Salary changes verified'
            }
            description={
              hasValidationErrors
                ? 'Please download the error report to review the errors and upload the corrected file.'
                : 'The uploaded file has been verified successfully. You can now submit the salary changes.'
            }
            link={{
              to: hasValidationErrors
                ? validationData?.errorsUrl
                : validationData?.successUrl,
              text: 'Download report',
            }}
          />
        )}
      </Stack>
    );
  };

  return (
    <Stack gap="32">
      <Typography as="h3" size="20" weight="medium" hideParagraphSpacing>
        Complete and upload template
      </Typography>
      <SelectEmployeesStep />
      <DownloadTemplateStep disabled={selectedEmployees?.length === 0} />
      <UploadTemplateStep disabled={!isSuccessDownloadTemplate} />
      <Inline justify={'flex-end'}>
        <Button
          disabled={!canSubmit}
          onClick={() =>
            submitChanges({
              companyId,
              type: BulkCompensationType.SALARY,
              url: fileUrl,
            })
          }
          loading={isSubmittingChanges}
        >
          Submit
        </Button>
      </Inline>
    </Stack>
  );
};
